import { useState, useEffect, useMemo, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Zap, Shield, Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  getBasePrice,
  getMediaAddon,
  formatBRL,
  type CertificateType,
  type ValidityType,
  type MediaType,
  type PersonType,
} from "@/lib/products";
import { findCertificateProduct, loadCatalogProducts } from "@/lib/catalog";
import { FlashBuyButton } from "@/components/FlashBuyButton";
import { getSocialProof, type SocialProof } from "@/api/client";
import type { Product } from "@/types/api";

export const ProductSelector = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [socialProof, setSocialProof] = useState<SocialProof | null>(null);

  // Default A1 / 1-ano / arquivo — a combinação mais vendida e simples de
  // comunicar visualmente. O usuário pode trocar a qualquer momento; os
  // selects continuam funcionais. Isso remove o estado "—" vazio ao carregar.
  const [certTypePF, setCertTypePF] = useState<CertificateType>("A1");
  const [validityPF, setValidityPF] = useState<ValidityType>("1-ano");
  const [mediaPF, setMediaPF] = useState<MediaType>("instalado");

  const [certTypePJ, setCertTypePJ] = useState<CertificateType>("A1");
  const [validityPJ, setValidityPJ] = useState<ValidityType>("1-ano");
  const [mediaPJ, setMediaPJ] = useState<MediaType>("instalado");

  const [certTypeMEI, setCertTypeMEI] = useState<CertificateType>("A1");
  const [validityMEI, setValidityMEI] = useState<ValidityType>("1-ano");
  const [mediaMEI, setMediaMEI] = useState<MediaType>("instalado");

  const navigate = useNavigate();

  useEffect(() => {
    void loadCatalogProducts()
      .then((catalog) => setProducts(catalog))
      .catch(() => toast.error("Erro ao carregar produtos"))
      .finally(() => setLoading(false));
    void getSocialProof()
      .then(setSocialProof)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (certTypePF === "A1") {
      setValidityPF("1-ano");
      setMediaPF("instalado");
    } else if (certTypePF === "A3") {
      setValidityPF("2-anos");
      setMediaPF("token");
    }
  }, [certTypePF]);

  useEffect(() => {
    if (certTypePJ === "A1") {
      setValidityPJ("1-ano");
      setMediaPJ("instalado");
    } else if (certTypePJ === "A3") {
      setValidityPJ("2-anos");
      setMediaPJ("token");
    }
  }, [certTypePJ]);

  useEffect(() => {
    if (certTypeMEI === "A1") {
      setValidityMEI("1-ano");
      setMediaMEI("instalado");
    } else if (certTypeMEI === "A3") {
      setValidityMEI("2-anos");
      setMediaMEI("token");
    }
  }, [certTypeMEI]);

  const personKindMap: Record<PersonType, "ECPF" | "ECNPJ" | "EMEI"> = {
    PF: "ECPF",
    PJ: "ECNPJ",
    MEI: "EMEI",
  };

  /**
   * Returns live pricing for the given selection, pulling from the catalog
   * (which already includes promotions applied by the backend). Falls back
   * to static base prices if the catalog isn't loaded yet.
   */
  const getPriceInfo = (
    personType: PersonType,
    certType: CertificateType,
    validity: ValidityType,
    media: MediaType
  ): { price: string; originalPrice: string | null; discountPct: number | null } => {
    if (!certType) return { price: "", originalPrice: null, discountPct: null };

    const kind = personKindMap[personType];
    const product = findCertificateProduct(products, kind, certType as "A1" | "A3");

    // A1: single price
    if (certType === "A1") {
      if (product) {
        const hasPromo = product.originalPriceCents && product.originalPriceCents > product.priceCents;
        return {
          price: formatBRL(product.priceCents / 100),
          originalPrice: hasPromo ? formatBRL(product.originalPriceCents! / 100) : null,
          discountPct: hasPromo
            ? Math.round((1 - product.priceCents / product.originalPriceCents!) * 100)
            : null,
        };
      }
      return {
        price: formatBRL(getBasePrice(personType, certType, "1-ano")),
        originalPrice: null,
        discountPct: null,
      };
    }

    // A3: requires media to compute final price
    if (!validity || !media) return { price: "", originalPrice: null, discountPct: null };

    if (product) {
      const tokenAddon = media === "token" ? getMediaAddon("token") * 100 : 0;
      const effective = product.priceCents + tokenAddon;
      const hasPromo = product.originalPriceCents && product.originalPriceCents > product.priceCents;
      const original = hasPromo ? product.originalPriceCents! + tokenAddon : null;
      return {
        price: formatBRL(effective / 100),
        originalPrice: original ? formatBRL(original / 100) : null,
        discountPct: hasPromo
          ? Math.round((1 - product.priceCents / product.originalPriceCents!) * 100)
          : null,
      };
    }

    return {
      price: formatBRL(getBasePrice(personType, certType, validity) + getMediaAddon(media)),
      originalPrice: null,
      discountPct: null,
    };
  };

  const handleViewProduct = (personType: PersonType, certType: CertificateType) => {
    const kind = personKindMap[personType];
    const certificate = findCertificateProduct(products, kind, certType as "A1" | "A3");

    if (!certificate) {
      toast.error("Produto de certificado não encontrado no catálogo.");
      return;
    }

    // Take the user to the product page — the final checkout happens there.
    navigate(`/produto/${certificate.slug}`);
  };

  const pricePF = useMemo(
    () => getPriceInfo("PF", certTypePF, validityPF, mediaPF),
    [certTypePF, validityPF, mediaPF, products]
  );
  const pricePJ = useMemo(
    () => getPriceInfo("PJ", certTypePJ, validityPJ, mediaPJ),
    [certTypePJ, validityPJ, mediaPJ, products]
  );
  const priceMEI = useMemo(
    () => getPriceInfo("MEI", certTypeMEI, validityMEI, mediaMEI),
    [certTypeMEI, validityMEI, mediaMEI, products]
  );

  const isPFComplete = certTypePF && validityPF && mediaPF;
  const isPJComplete = certTypePJ && validityPJ && mediaPJ;
  const isMEIComplete = certTypeMEI && validityMEI && mediaMEI;

  if (loading) {
    return (
      <section id="produtos" className="py-12 md:py-20 bg-muted/50">
        <div className="container">
          <div className="text-center">Carregando produtos...</div>
        </div>
      </section>
    );
  }

  const renderCard = (
    title: string,
    subtitle: string,
    colorClass: string,
    certType: CertificateType,
    setCertType: (value: CertificateType) => void,
    validity: ValidityType,
    setValidity: (value: ValidityType) => void,
    media: MediaType,
    setMedia: (value: MediaType) => void,
    priceInfo: { price: string; originalPrice: string | null; discountPct: number | null },
    complete: boolean,
    personType: PersonType,
    bestSeller: boolean
  ) => (
    <Card className={`relative overflow-hidden shadow-lg transition-all hover:shadow-xl ${bestSeller ? "ring-2 ring-warning/60" : ""}`}>
      {bestSeller && (
        <div className="absolute -right-12 top-6 z-10 rotate-45 bg-warning px-12 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-md">
          ★ Mais vendido
        </div>
      )}
      <div className={`${colorClass} text-primary-foreground px-4 py-3 md:px-6 md:py-4 text-center`}>
        <h3 className="text-base md:text-xl font-bold">{title}</h3>
        <p className="text-[11px] md:text-xs opacity-90 mt-0.5">{subtitle}</p>
      </div>

      <div className="p-4 md:p-6 space-y-3 md:space-y-5">

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block uppercase tracking-wider">Tipo</label>
            <Select value={certType} onValueChange={(v) => setCertType(v as CertificateType)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A1">A1</SelectItem>
                <SelectItem value="A3">A3</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block uppercase tracking-wider">Validade</label>
            <div className="flex h-10 items-center justify-center rounded-md border bg-muted/30 px-3 text-sm font-semibold text-foreground">
              {certType === "A1" ? "1 ano" : certType === "A3" ? "2 anos" : "—"}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block uppercase tracking-wider">Mídia</label>
            {certType === "A1" ? (
              // A1 é sempre arquivo instalado — mostra readonly em vez de select
              // desabilitado pra manter consistência visual com o campo Validade.
              <div className="flex h-10 items-center justify-center rounded-md border bg-muted/30 px-3 text-sm font-semibold text-foreground">
                Computador
              </div>
            ) : (
              <Select
                value={media}
                onValueChange={(v) => setMedia(v as MediaType)}
                disabled={!certType}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sem-midia">Em nuvem</SelectItem>
                  <SelectItem value="token">Com token</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>


        <div className="text-center py-1 md:py-2">
          {priceInfo.price ? (
            <>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Investimento</p>
              {priceInfo.originalPrice && (
                <p className="text-sm text-muted-foreground line-through tabular-nums">
                  {priceInfo.originalPrice}
                </p>
              )}
              <div className="text-3xl md:text-4xl font-extrabold text-foreground tabular-nums">
                {priceInfo.price}
              </div>
              {priceInfo.discountPct !== null && (
                <span className="inline-flex items-center gap-1 mt-1 rounded-full bg-success/15 px-2.5 py-0.5 text-[11px] font-bold text-success">
                  −{priceInfo.discountPct}% OFF
                </span>
              )}
              <p className="text-[10px] text-muted-foreground mt-1">à vista no PIX · até 12x no cartão</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground py-3 md:py-4">Selecione tipo {certType === "A3" ? "e mídia " : ""}para ver o preço</p>
          )}
        </div>

        <div className="space-y-2">
          {/* Compra Flash — resolve o produto específico do card (PF/PJ/MEI + A1/A3)
              e vai direto pro checkout. Fica acima do "Ver produto" pra sinalizar
              que é o caminho rápido. */}
          {complete && (() => {
            const kind = personKindMap[personType];
            const specific = findCertificateProduct(products, kind, certType as "A1" | "A3");
            return specific ? (
              <FlashBuyButton product={specific} fullWidth size="lg" />
            ) : null;
          })()}
          <Button
            className="w-full bg-primary hover:bg-primary/90 font-bold shadow-md hover:shadow-lg transition-all"
            size="lg"
            variant={complete ? "outline" : "default"}
            onClick={() => handleViewProduct(personType, certType)}
            disabled={!complete}
          >
            {complete ? "Ver produto" : "Selecione as opções"}
          </Button>
        </div>
      </div>
    </Card>
  );

  return (
    <section id="produtos" className="py-6 md:py-20 bg-muted/50">
      <div className="container">
        {/* Mobile: tabs PJ/PF/MEI + 1 card do ativo (evita scroll de ~1200px empilhado) */}
        <div className="md:hidden max-w-md mx-auto">
          <MobileTabsSelector
            pj={renderCard("Pessoa Jurídica", "Para CNPJ · NF-e, eSocial, SEFAZ", "bg-success", certTypePJ, setCertTypePJ, validityPJ, setValidityPJ, mediaPJ, setMediaPJ, pricePJ, Boolean(isPJComplete), "PJ", true)}
            pf={renderCard("Pessoa Física", "Para CPF · IRPF, e-CAC, Gov.br", "bg-primary", certTypePF, setCertTypePF, validityPF, setValidityPF, mediaPF, setMediaPF, pricePF, Boolean(isPFComplete), "PF", false)}
            mei={renderCard("MEI", "Microempreendedor · NFS-e, DASN", "bg-secondary", certTypeMEI, setCertTypeMEI, validityMEI, setValidityMEI, mediaMEI, setMediaMEI, priceMEI, Boolean(isMEIComplete), "MEI", false)}
          />
        </div>

        {/* Desktop: 3 cards lado a lado */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {renderCard("Pessoa Jurídica", "Para CNPJ · NF-e, eSocial, SEFAZ", "bg-success", certTypePJ, setCertTypePJ, validityPJ, setValidityPJ, mediaPJ, setMediaPJ, pricePJ, Boolean(isPJComplete), "PJ", true)}
          {renderCard("Pessoa Física", "Para CPF · IRPF, e-CAC, Gov.br", "bg-primary", certTypePF, setCertTypePF, validityPF, setValidityPF, mediaPF, setMediaPF, pricePF, Boolean(isPFComplete), "PF", false)}
          {renderCard("MEI", "Microempreendedor · NFS-e, DASN", "bg-secondary", certTypeMEI, setCertTypeMEI, validityMEI, setValidityMEI, mediaMEI, setMediaMEI, priceMEI, Boolean(isMEIComplete), "MEI", false)}
        </div>
      </div>
    </section>
  );
};

/**
 * Mobile tabs selector — evita scroll gigante no mobile mostrando só o card
 * da aba ativa. PJ vem primeiro por ser o mais vendido.
 */
function MobileTabsSelector({
  pj,
  pf,
  mei,
}: {
  pj: React.ReactElement;
  pf: React.ReactElement;
  mei: React.ReactElement;
}) {
  const tabs: Array<{ key: "PJ" | "PF" | "MEI"; label: string; sub: string }> = [
    { key: "PJ", label: "Empresa", sub: "CNPJ" },
    { key: "PF", label: "Pessoa", sub: "CPF" },
    { key: "MEI", label: "MEI", sub: "CNPJ MEI" },
  ];
  const slides = [pj, pf, mei];
  const [active, setActive] = useState(0);

  // Embla com snap centralizado — swipe pra lateral faz alternar abas
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "center",
    containScroll: "trimSnaps",
  });

  // Mantém tab header sincronizado com slide ativo
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActive(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => void emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const goTo = useCallback(
    (idx: number) => {
      emblaApi?.scrollTo(idx);
      setActive(idx);
    },
    [emblaApi]
  );

  return (
    <div className="space-y-3">
      {/* Tabs — bordas mais nítidas + indicador de active com ring */}
      <div
        role="tablist"
        className="grid grid-cols-3 gap-2 p-1.5 rounded-2xl border-2 border-border bg-muted/40"
      >
        {tabs.map((t, i) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active === i}
            onClick={() => goTo(i)}
            className={`relative rounded-xl px-2 py-2 text-center transition-all duration-200 ${
              active === i
                ? "bg-white border-2 border-primary shadow-sm font-bold text-primary"
                : "border-2 border-transparent text-muted-foreground active:scale-95"
            }`}
          >
            <span className="block text-sm leading-tight">{t.label}</span>
            <span
              className={`block text-[10px] mt-0.5 ${
                active === i ? "text-primary/70" : "text-muted-foreground/80"
              }`}
            >
              {t.sub}
            </span>
            {t.key === "PJ" && active !== i && (
              <span className="absolute -top-1.5 -right-1.5 rounded-full bg-warning px-1.5 py-0.5 text-[8px] font-bold text-white shadow-sm">
                ★
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Hint de swipe — some depois da primeira interação */}
      {active === 0 && (
        <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
          <span>←</span>
          <span>Arraste pros lados pra alternar</span>
          <span>→</span>
        </p>
      )}

      {/* Embla carousel — cada slide é 100% de largura, snap centralizado */}
      <div className="overflow-hidden -mx-4 px-4" ref={emblaRef}>
        <div className="flex touch-pan-y">
          {slides.map((s, i) => (
            <div
              key={i}
              className="flex-[0_0_100%] min-w-0 pr-2 last:pr-0 pl-2 first:pl-0"
            >
              {s}
            </div>
          ))}
        </div>
      </div>

      {/* Indicadores (pontos) — feedback visual de qual aba está ativa */}
      <div className="flex items-center justify-center gap-1.5 pt-1">
        {tabs.map((t, i) => (
          <button
            key={t.key}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Ir para ${t.label}`}
            className={`h-1.5 rounded-full transition-all ${
              active === i ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
